import type {
  Interpretation,
  InterpretationRequest,
  SemanticInterpreter,
  TraitReading,
} from './types'

const KNOWN_TRAITS = [
  'bounded',
  'gentle',
  'named',
  'cyclic',
  'timed',
  'remembering',
  'reciprocal',
  'reflective',
  'released',
  'forceful',
  'endless',
  'hungry',
  'forgetful',
] as const

type KnownTrait = (typeof KNOWN_TRAITS)[number]

interface SemanticSignals {
  readonly boundaryFrame: boolean
  readonly closure: boolean
  readonly lowIntensity: boolean
  readonly guidedMotion: boolean
  readonly identity: boolean
  readonly designation: boolean
  readonly recurrence: boolean
  readonly linkage: boolean
  readonly temporalMarker: boolean
  readonly condition: boolean
  readonly pastReference: boolean
  readonly retention: boolean
  readonly transfer: boolean
  readonly response: boolean
  readonly surface: boolean
  readonly redirection: boolean
  readonly heldObject: boolean
  readonly release: boolean
  readonly compulsion: boolean
  readonly totalizer: boolean
  readonly aggressiveAction: boolean
  readonly unlimited: boolean
  readonly continuation: boolean
  readonly consumption: boolean
  readonly renewal: boolean
  readonly erasure: boolean
  readonly blockedClosure: boolean
  readonly blockedRelease: boolean
}

const TRAIT_EVIDENCE: Readonly<Record<KnownTrait, string>> = {
  bounded:
    '공간을 한정하는 틀과 작동이 끝나는 사건이 함께 있어, 현상을 유한한 범위로 재구성했습니다.',
  gentle:
    '낮은 강도의 방식과 연속적인 이동이 결합되어, 충돌보다 유도가 앞서는 운동으로 보았습니다.',
  named:
    '정체성을 가리키는 표지와 호명 행위가 연결되어, 대상이 구별 가능한 하나의 개체가 됩니다.',
  cyclic:
    '회귀하는 궤적과 앞뒤를 잇는 관계가 함께 나타나, 운동이 출발점으로 돌아오는 구조가 됩니다.',
  timed:
    '순서 또는 횟수 표지가 조건절과 결합되어, 변화가 일어나는 시점을 특정합니다.',
  remembering:
    '이전 상태를 가리키는 참조와 그것을 보존하는 관계가 연결되어, 과거의 소유 흔적이 유지됩니다.',
  reciprocal:
    '전달 행위와 상대의 응답이 한 구조 안에 있어, 의미가 일방향으로 머물지 않습니다.',
  reflective:
    '상을 담는 매개와 방향 전환이 결합되어, 받은 의미를 변형해 되비추는 관계가 됩니다.',
  released:
    '붙들린 대상과 소유를 끝내는 행위가 연결되어, 구속 이후의 이탈이 성립합니다.',
  forceful:
    '강제 조건과 저항을 밀어내는 행동이 함께 있어, 관계가 합의보다 압력으로 작동합니다.',
  endless:
    '한계를 지우는 범위와 계속되는 동작이 결합되어, 종료 조건이 없는 진행으로 해석됩니다.',
  hungry:
    '소비 행위가 다음 대상 또는 추가 요구와 이어져, 충족 뒤에도 필요가 갱신됩니다.',
  forgetful:
    '정체성 표지와 그것을 없애거나 비워 둔 관계가 연결되어, 되돌아갈 기준이 소실됩니다.',
}

const TRAIT_CONFLICTS: Readonly<
  Partial<Record<KnownTrait, readonly KnownTrait[]>>
> = {
  bounded: ['endless'],
  gentle: ['forceful'],
  named: ['forgetful'],
  timed: ['endless'],
  remembering: ['forgetful'],
  reciprocal: ['hungry'],
  reflective: ['forgetful'],
  released: ['hungry'],
  forceful: ['gentle'],
  endless: ['bounded', 'timed'],
  hungry: ['reciprocal', 'released'],
  forgetful: ['named', 'remembering', 'reflective'],
}

function has(text: string, pattern: RegExp): boolean {
  return pattern.test(text)
}

/**
 * Positive signals must not leak out of a negated Korean predicate. Keeping
 * the object words intact still lets a later affirmative action refer to
 * them, while e.g. `지우지 않는다` can no longer count as erasure.
 */
function withoutNegatedPredicates(text: string): string {
  return text.replace(/[가-힣]+지\s*(?:않|못)[가-힣]*/g, ' ')
}

function analyzeSignals(text: string): SemanticSignals {
  const affirmedText = withoutNegatedPredicates(text)
  const preservedIdentity = has(
    text,
    /(?:지우|잊|사라지)[가-힣]*지\s*(?:않|못)/,
  )
  const deniedResistance = has(
    text,
    /저항을?\s*허용(?:하)?지\s*(?:않|못)/,
  )
  const blockedClosure = has(
    text,
    /(?:멎|멈추|닫히|잠들|끝나)\s*지\s*(?:않|못)|(?:멎|멈추|닫히|잠들|끝나)\s*지\s*않고/,
  )
  const blockedRelease = has(
    text,
    /(?:놓아주|풀어주|돌려주|돌려보내)\s*지\s*(?:않|못)|(?:놓아주|풀어주|돌려주)\s*지\s*않고/,
  )

  return {
    boundaryFrame: has(
      affirmedText,
      /접어\s*둔|리본|손바닥\s*안|고리\s*안|상자\s*안|가장자리|경계|범위|매듭/,
    ),
    closure:
      !blockedClosure &&
      has(affirmedText, /멎|멈추|닫히|닫힌|닫혀|잠들|잠든|끝나|끝난|수렴|종결/),
    lowIntensity: has(affirmedText, /숨결|천천히|부드럽|살며시|느리|낮은\s*장력/),
    guidedMotion: has(affirmedText, /따라|감기|둥글|흐르|접히|스미|한\s*바퀴/),
    identity: has(
      affirmedText,
      /이름|호칭|주인|원본|얼굴|흔적|약속|매듭|[‘“'"][^’”'"]+[’”'"]/,
    ),
    designation: has(
      affirmedText,
      /부르|불러|붙이|명명|(?:이라|라고)\s*(?:하|부르|불러)|라는\s*이름|이름을?\s*받/,
    ),
    recurrence: has(affirmedText, /둥글|되돌|되풀이|반복|한\s*바퀴|돌고|뒤집/),
    linkage: has(affirmedText, /따라|다시|원래|받은|시작/),
    temporalMarker: has(
      affirmedText,
      /첫(?:\s|매듭)|한\s*번|두\s*번|제시간|박자|순서|마지막|다음\s*(?:울림|차례)|번째/,
    ),
    condition: has(affirmedText, /(?:으)?면|때|후|뒤|마다|까지|하자|고\s*나서/),
    pastReference: has(
      affirmedText,
      /오래된|앞선|이전|과거|빌린|흔적|원본/,
    ),
    retention:
      preservedIdentity ||
      has(affirmedText, /약속|기억|간직|보존|남겨|되새/),
    transfer: has(
      affirmedText,
      /돌려주|돌려준|돌려줘|되돌려주|반환|건네|주인에게\s*보내|놓아주|놓아준|놓아줘/,
    ),
    response: has(affirmedText, /답|응답|노크|받(?:은|고|으면)|주인에게|서로|한\s*번씩/),
    surface: has(affirmedText, /거울|표면|뒷면|반사면|얼굴|그림자/),
    redirection: has(affirmedText, /뒤집|비추|반사|거꾸로|반대로|방향을?\s*바꿔/),
    heldObject: has(affirmedText, /붙잡|붙들|가두|묶|빌린|받은|훔친|이름|얼굴|소유/),
    release:
      !blockedRelease &&
      has(
        affirmedText,
        /놓아주|놓아준|놓아줘|풀어주|풀어준|돌려보내|해방|보내\s*주|돌려준다/,
      ),
    compulsion: has(
      affirmedText,
      /억지|강제로|해야만|명령/,
    ) || deniedResistance,
    totalizer: has(affirmedText, /모든|모조리|전부|누구도|아무도/),
    aggressiveAction: has(
      affirmedText,
      /끌어당(?:기|긴|겨)|밀어붙|베어|부수|깨뜨|삼키|붙잡|붙들|차지|지우|덮치|가두/,
    ),
    unlimited: has(
      text,
      /끝없|영원|한없이|계속|바닥\s*없|마지막이?\s*없|끝나지|멎지|닫히지|모든\s*(?:얼굴|이름|것)/,
    ),
    continuation: has(
      affirmedText,
      /번지|번진|번져|이어|울리|붙잡|붙들|기다리|되풀이|반복|요구|끌어당(?:기|긴|겨)|남아|커지/,
    ),
    consumption: has(affirmedText, /삼키|먹어|먹고|허기|배고|굶주|소비/),
    renewal: has(affirmedText, /다음|더\s*많|또\s|계속|마다|새로운|요구/),
    erasure: has(
      affirmedText,
      /지우|잊|이름\s*없이|주인\s*없이|주인\s*없는|원본\s*없이|사라지|대신\s*(?:그\s*)?자리/,
    ),
    blockedClosure,
    blockedRelease,
  }
}

function inferSegment(text: string): ReadonlySet<KnownTrait> {
  const signal = analyzeSignals(text)
  const traits = new Set<KnownTrait>()

  if (signal.boundaryFrame && signal.closure) traits.add('bounded')
  if (signal.lowIntensity && signal.guidedMotion) traits.add('gentle')
  if (signal.identity && signal.designation && !signal.erasure) {
    traits.add('named')
  }
  if (signal.recurrence && signal.linkage) traits.add('cyclic')
  if (signal.temporalMarker && signal.condition) traits.add('timed')
  if (
    signal.pastReference &&
    (signal.retention || signal.transfer) &&
    !signal.erasure
  ) {
    traits.add('remembering')
  }
  if (signal.transfer && signal.response && !signal.blockedRelease) {
    traits.add('reciprocal')
  }
  if (signal.surface && signal.redirection && !signal.erasure) {
    traits.add('reflective')
  }
  if (signal.heldObject && signal.release && !signal.blockedRelease) {
    traits.add('released')
  }
  if (
    (signal.compulsion || signal.totalizer) &&
    signal.aggressiveAction
  ) {
    traits.add('forceful')
  }
  if (
    (signal.unlimited && signal.continuation) ||
    (signal.blockedClosure && signal.continuation)
  ) {
    traits.add('endless')
  }
  if (
    signal.consumption &&
    (signal.renewal || signal.totalizer || signal.continuation)
  ) {
    traits.add('hungry')
  }
  if (signal.identity && signal.erasure) traits.add('forgetful')

  return traits
}

function lastReversal(
  text: string,
): Readonly<{ index: number; length: number }> | null {
  const markers = /하지만|그러나|그런데|대신|아니라|지만/g
  let latest: RegExpExecArray | null = null
  let match = markers.exec(text)

  while (match !== null) {
    latest = match
    match = markers.exec(text)
  }

  return latest === null
    ? null
    : { index: latest.index, length: latest[0].length }
}

function mergeWithReversal(
  earlier: ReadonlySet<KnownTrait>,
  later: ReadonlySet<KnownTrait>,
): ReadonlySet<KnownTrait> {
  const merged = new Set<KnownTrait>(earlier)

  for (const trait of KNOWN_TRAITS) {
    if (!later.has(trait)) continue
    for (const conflict of TRAIT_CONFLICTS[trait] ?? []) {
      merged.delete(conflict)
    }
    merged.add(trait)
  }

  return merged
}

function inferTraits(text: string): ReadonlySet<KnownTrait> {
  const reversal = lastReversal(text)
  if (reversal === null) return inferSegment(text)

  const earlier = inferSegment(text.slice(0, reversal.index))
  const later = inferSegment(text.slice(reversal.index + reversal.length))
  return mergeWithReversal(earlier, later)
}

function anomalyKind(id: string): 'thread' | 'bell' | 'mirror' {
  if (id.includes('mirror')) return 'mirror'
  if (id.includes('bell')) return 'bell'
  return 'thread'
}

function manifestationFor(
  kind: 'thread' | 'bell' | 'mirror',
  traits: ReadonlySet<KnownTrait>,
): string {
  const dangerous = ['forceful', 'endless', 'hungry', 'forgetful'].some(
    (trait) => traits.has(trait as KnownTrait),
  )

  if (traits.size === 0) {
    return '해석 가능한 관계가 충분히 연결되지 않아 현상이 원래의 긴장을 유지합니다.'
  }

  if (dangerous) {
    const subject = {
      thread: '실의 장력이 격리선 밖으로 뻗고',
      bell: '울림이 응답의 자리를 덮으며',
      mirror: '표면이 원본과 반사의 경계를 잠식하며',
    }[kind]
    const consequence = traits.has('hungry')
      ? '소비가 끝날 때마다 다음 대상을 요구합니다.'
      : traits.has('endless')
        ? '종결 조건보다 지속 조건이 우세해집니다.'
        : '관계가 교환이 아니라 일방적인 압력으로 굳어집니다.'
    return subject + ' ' + consequence
  }

  if (kind === 'thread') {
    return traits.has('bounded')
      ? '실의 운동이 한정된 궤도로 수렴합니다. 범위와 종결 사건이 서로를 지지합니다.'
      : '실의 움직임은 낮아졌지만 종결 위치는 아직 하나로 모이지 않습니다.'
  }
  if (kind === 'bell') {
    return traits.has('reciprocal')
      ? '울림이 빌린 흔적을 상대에게 되돌립니다. 응답이 시간의 고리를 닫습니다.'
      : '울림은 과거의 흔적을 붙들지만 되돌아갈 상대를 아직 찾지 못합니다.'
  }
  return traits.has('reflective')
    ? '표면이 받은 의미의 방향만 바꾼 뒤 소유권을 원래 자리로 돌립니다.'
    : '표면에 의미가 남았지만 원본과 상을 구분할 방향이 아직 없습니다.'
}

function readingFor(trait: KnownTrait): TraitReading {
  return {
    trait,
    evidence: TRAIT_EVIDENCE[trait],
  }
}

/**
 * Deterministic fallback meaning interpreter. It combines relational signals,
 * clause structure, and late contrast overrides; chip ids never carry meaning.
 */
export class StubSemanticInterpreter implements SemanticInterpreter {
  async interpret(request: InterpretationRequest): Promise<Interpretation> {
    const normalizedMetaphor = request.metaphor
      .normalize('NFKC')
      .replace(/\s+/g, ' ')
      .trim()
    const inferred = inferTraits(normalizedMetaphor)
    const canonicalTraits = request.traitVocabulary.filter(
      (trait): trait is KnownTrait =>
        KNOWN_TRAITS.includes(trait as KnownTrait) &&
        inferred.has(trait as KnownTrait),
    )
    const inferredCanonical = new Set<KnownTrait>(canonicalTraits)
    const inheritedCount = request.inheritedTraits.filter((trait) =>
      request.traitVocabulary.includes(trait),
    ).length

    return Promise.resolve({
      requestId: request.requestId,
      canonicalTraits,
      manifestation: manifestationFor(
        anomalyKind(request.anomalyId),
        inferredCanonical,
      ),
      readings: canonicalTraits.map(readingFor),
      afterimage:
        inheritedCount > 0
          ? '앞선 안정화의 ' +
            inheritedCount +
            '개 성질은 직접 해석과 분리된 후광으로 남습니다.'
          : '이번 해석은 현재 문장 안에서 연결된 관계만으로 구성되었습니다.',
    })
  }
}
